import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { useAppStore, sel } from './store/useAppStore.js';
import { BottomNav } from './components/layout/BottomNav.jsx';
import { OfflineBanner } from './components/ui/OfflineBanner.jsx';
import { ScreenErrorBoundary } from './components/errors/ErrorBoundary.jsx';
import { Skeleton } from './components/ui/Skeleton.jsx';
import { GuestGuard } from './components/auth/GuestGuard.jsx';
import { useCapacitorDeepLink } from './hooks/useDeepLink.js';
import { useScreenTracking } from './hooks/useAnalytics.js';
import { getUserMessage } from './lib/errors/AppError.js';

// ── Code-split all screens ────────────────────────────────────────────────────
const HomeScreen     = lazy(() => import('./screens/HomeScreen.jsx').then((m) => ({ default: m.HomeScreen })));
const ScanScreen     = lazy(() => import('./screens/ScanScreen.jsx').then((m) => ({ default: m.ScanScreen })));
const ItemsScreen    = lazy(() => import('./screens/ItemsScreen.jsx').then((m) => ({ default: m.ItemsScreen })));
const SessionScreen  = lazy(() => import('./screens/SessionScreen.jsx').then((m) => ({ default: m.SessionScreen })));
const SummaryScreen  = lazy(() => import('./screens/SummaryScreen.jsx').then((m) => ({ default: m.SummaryScreen })));
const JoinScreen     = lazy(() => import('./screens/JoinScreen.jsx').then((m) => ({ default: m.JoinScreen })));
const AuthScreen     = lazy(() => import('./screens/AuthScreen.jsx').then((m) => ({ default: m.AuthScreen })));
const SettingsScreen = lazy(() => import('./screens/SettingsScreen.jsx').then((m) => ({ default: m.SettingsScreen })));
const HistoryScreen    = lazy(() => import('./screens/HistoryScreen.jsx').then((m) => ({ default: m.HistoryScreen })));
const BetaReportScreen = lazy(() => import('./screens/BetaReportScreen.jsx').then((m) => ({ default: m.BetaReportScreen })));
const WalkthroughScreen = lazy(() => import('./screens/WalkthroughScreen.jsx').then((m) => ({ default: m.WalkthroughScreen })));
const LedgerScreen     = lazy(() => import('./screens/LedgerScreen.jsx').then((m) => ({ default: m.LedgerScreen })));

// ── QueryClient with error boundary integration ───────────────────────────────
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry:     1,
      staleTime: 30_000,
      onError:   (err) => {
        if (import.meta.env.DEV) console.error('[Query error]', err);
      },
    },
    mutations: {
      onError: (err) => {
        if (import.meta.env.DEV) console.error('[Mutation error]', err);
      },
    },
  },
});

// ── Screen loading fallback ───────────────────────────────────────────────────
const ScreenLoader = () => (
  <div className="min-h-dvh bg-surface flex items-center justify-center">
    <div className="text-4xl animate-bounce-soft">⚡</div>
  </div>
);

// ── Per-screen error fallback ─────────────────────────────────────────────────
const withBoundary = (screen, element) => (
  <ScreenErrorBoundary screenName={screen}>
    <Suspense fallback={<ScreenLoader />}>
      {element}
    </Suspense>
  </ScreenErrorBoundary>
);

// Host-only routes — guests get bounced to /session or /join.
const hostOnly = (screen, element) => withBoundary(screen, <GuestGuard>{element}</GuestGuard>);

// ── Deep link + analytics wiring ─────────────────────────────────────────────
function AppEffects() {
  const navigate = useNavigate();
  const location = useLocation();
  useCapacitorDeepLink(navigate);
  useEffect(() => {
    try {
      const seen = localStorage.getItem('walkthroughSeen');
      const path = location.pathname;
      const skip = path.startsWith('/join') || path.startsWith('/auth') || path === '/walkthrough';
      if (!seen && !skip) navigate('/walkthrough', { replace: true });
    } catch {}
  }, []);
  // Screen tracking is called inside each screen via useScreenTracking()
  // This component just handles cross-cutting effects
  return null;
}

// ── Shell ─────────────────────────────────────────────────────────────────────
function AppShell() {
  const initAuth    = useAppStore((s) => s.initAuth);
  const authLoading = useAppStore((s) => s.authLoading);

  useEffect(() => { initAuth(); }, []);

  if (authLoading) {
    return (
      <div className="min-h-dvh bg-surface flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="text-5xl animate-bounce-soft">⚡</div>
          <p className="text-white/30 text-sm font-bold tracking-widest uppercase">Loading</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-surface max-w-[430px] mx-auto relative">
      <OfflineBanner />
      <AppEffects />
      <Routes>
        <Route path="/auth"         element={withBoundary('Auth',     <AuthScreen />)} />
        <Route path="/"             element={hostOnly('Home',     <HomeScreen />)} />
        <Route path="/scan"         element={hostOnly('Scan',     <ScanScreen />)} />
        <Route path="/items"        element={hostOnly('Items',    <ItemsScreen />)} />
        <Route path="/session"      element={withBoundary('Session',  <SessionScreen />)} />
        <Route path="/session/:id"  element={withBoundary('Session',  <SessionScreen />)} />
        <Route path="/summary"      element={withBoundary('Summary',  <SummaryScreen />)} />
        <Route path="/ledger"       element={hostOnly('Ledger',   <LedgerScreen />)} />
        <Route path="/join"         element={withBoundary('Join',     <JoinScreen />)} />
        <Route path="/join/:code"   element={withBoundary('Join',     <JoinScreen />)} />
        <Route path="/history"      element={hostOnly('History',  <HistoryScreen />)} />
        <Route path="/settings"     element={hostOnly('Settings', <SettingsScreen />)} />
        <Route path="/beta-report"  element={hostOnly('BetaReport', <BetaReportScreen />)} />
        <Route path="/walkthrough"  element={withBoundary('Walkthrough', <WalkthroughScreen />)} />
        <Route path="*"             element={<Navigate to="/" replace />} />
      </Routes>
      <BottomNav />
    </div>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppShell />
        <Toaster
          position="top-center"
          toastOptions={{
            duration: 3000,
            style: {
              background:   '#18181f',
              color:        '#fff',
              border:       '1px solid rgba(255,255,255,0.1)',
              borderRadius: '14px',
              fontFamily:   "'DM Sans', sans-serif",
              fontWeight:   700,
              fontSize:     '14px',
              maxWidth:     '360px',
            },
            success: { iconTheme: { primary: '#00D68F', secondary: '#18181f' } },
            error:   { iconTheme: { primary: '#FF6B6B', secondary: '#18181f' } },
          }}
        />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
